import html2canvas from 'html2canvas'

export const CAPTURE_SCALE = 2

// Browsers cap how large a single <canvas> may be. Past the cap the element is still created
// with the requested width/height, but its backing store is never allocated, so every draw is a
// no-op and the canvas reads back fully transparent — which jsPDF then encodes as solid BLACK,
// because JPEG has no alpha channel. Tall tabs (History, with every revision group expanded) are
// the ones that reach it, so scale down rather than emit black pages.
// 16384px per side is the smallest limit across Chrome/Firefox/Safari on desktop; the area cap is
// ours, to keep one canvas under ~256MB of RGBA backing store (we hold two at once while the
// header is drawn).
const MAX_CANVAS_DIMENSION_PX = 16384
const MAX_CANVAS_AREA_PX = 64_000_000

const HEADER_HEIGHT_PX = 90
const HEADER_FONT_SIZE_PX = 40
const HEADER_PADDING_LEFT_PX = 16

// The largest scale at which cssWidth x cssHeight still fits the canvas limits once
// addHeaderToCanvas has grown it by one header's worth of height.
const getSafeCaptureScale = (cssWidth, cssHeight, desiredScale) => {
  const totalCssHeight = cssHeight + HEADER_HEIGHT_PX

  const widthLimitedScale = MAX_CANVAS_DIMENSION_PX / cssWidth
  const heightLimitedScale = MAX_CANVAS_DIMENSION_PX / totalCssHeight
  const areaLimitedScale = Math.sqrt(MAX_CANVAS_AREA_PX / (cssWidth * totalCssHeight))

  return Math.min(desiredScale, widthLimitedScale, heightLimitedScale, areaLimitedScale)
}

// A canvas whose backing store was never allocated reads back fully transparent. Sampling it at
// full size would cost hundreds of MB, so downscale into a thumbnail and inspect that instead.
const isCanvasBlank = (canvas) => {
  const probe = document.createElement('canvas')
  probe.width = 32
  probe.height = 32

  const context = probe.getContext('2d')
  context.drawImage(canvas, 0, 0, probe.width, probe.height)

  let data
  try {
    ;({ data } = context.getImageData(0, 0, probe.width, probe.height))
  } catch {
    // A cross-origin image in the capture taints the canvas and makes it unreadable. That says
    // nothing about whether it rendered, so let the capture through rather than failing the export.
    return false
  }

  for (let offset = 3; offset < data.length; offset += 4) {
    if (data[offset] !== 0) {
      return false
    }
  }
  return true
}

// Renders a DOM node to a canvas for print-quality output, at CAPTURE_SCALE where the element is
// small enough and at a reduced scale where it isn't.
export const captureElementAsCanvas = async (element, { scale = CAPTURE_SCALE, backgroundColor = '#ffffff' } = {}) => {
  if (!element) {
    throw new Error('captureElementAsCanvas: element is required')
  }

  // html2canvas clones the whole document into an offscreen iframe and measures the *clone* to
  // size the output canvas. That auto-measurement can come out zero even when the live element
  // clearly has size, so we measure the live element ourselves and pass width/height explicitly
  // to bypass html2canvas's own (unreliable) size detection.
  const rect = element.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) {
    throw new Error('captureElementAsCanvas: element has zero rendered size')
  }

  const width = Math.ceil(rect.width)
  const height = Math.ceil(rect.height)
  const effectiveScale = getSafeCaptureScale(width, height, scale)

  const canvas = await html2canvas(element, {
    scale: effectiveScale,
    backgroundColor,
    useCORS: true,
    logging: false,
    width,
    height,
    windowWidth: document.documentElement.clientWidth,
    windowHeight: document.documentElement.clientHeight,
  })

  if (canvas.width === 0 || canvas.height === 0) {
    throw new Error('captureElementAsCanvas: captured a zero-size canvas — the element had no rendered content')
  }

  if (isCanvasBlank(canvas)) {
    throw new Error(
      `captureElementAsCanvas: captured an empty ${canvas.width}x${canvas.height} canvas — ` +
        'the browser refused to allocate it. Reduce CAPTURE_SCALE or the captured area.'
    )
  }

  // addHeaderToCanvas has to draw its header at the same scale the content was captured at, and
  // that is no longer always CAPTURE_SCALE.
  canvas.dataset.captureScale = String(effectiveScale)

  return canvas
}

// Returns a new, taller canvas with the tab's name drawn as a header above the captured content,
// so each tab's page(s) in the merged PDF are labeled with which tab they came from.
export const addHeaderToCanvas = (canvas, label, { scale, backgroundColor = '#ffffff' } = {}) => {
  const capturedScale = Number(canvas.dataset?.captureScale)
  const effectiveScale = scale ?? (Number.isFinite(capturedScale) && capturedScale > 0 ? capturedScale : CAPTURE_SCALE)
  const headerHeightPx = Math.round(HEADER_HEIGHT_PX * effectiveScale)

  const labeledCanvas = document.createElement('canvas')
  labeledCanvas.width = canvas.width
  labeledCanvas.height = canvas.height + headerHeightPx

  const context = labeledCanvas.getContext('2d')
  context.fillStyle = backgroundColor
  context.fillRect(0, 0, labeledCanvas.width, labeledCanvas.height)

  const headerFontStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'

  context.fillStyle = '#1A202C'
  context.font = `600 ${HEADER_FONT_SIZE_PX * effectiveScale}px ${headerFontStack}`
  context.textBaseline = 'middle'
  context.fillText(label, HEADER_PADDING_LEFT_PX * effectiveScale, headerHeightPx / 2)

  context.drawImage(canvas, 0, headerHeightPx)

  if (isCanvasBlank(labeledCanvas)) {
    throw new Error(
      `addHeaderToCanvas: produced an empty ${labeledCanvas.width}x${labeledCanvas.height} canvas — ` +
        'the browser refused to allocate it.'
    )
  }

  labeledCanvas.dataset.captureScale = String(effectiveScale)

  return labeledCanvas
}

// Slices a tall canvas into page-sized chunks so a multi-page tab renders as multiple PDF pages
// instead of one oversized page. pageWidthPx/pageHeightPx describe one PDF page in canvas pixels
// (i.e. the target page size scaled by the same factor used to size the source canvas).
export const sliceCanvasToPageChunks = (canvas, { pageWidthPx, pageHeightPx }) => {
  if (pageWidthPx <= 0 || pageHeightPx <= 0) {
    throw new Error('sliceCanvasToPageChunks: pageWidthPx and pageHeightPx must be positive')
  }

  const scaledPageHeightPx = Math.round((canvas.width / pageWidthPx) * pageHeightPx)
  const pageCount = Math.max(1, Math.ceil(canvas.height / scaledPageHeightPx))

  const chunks = []
  for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
    const sourceY = pageIndex * scaledPageHeightPx
    const sourceHeight = Math.min(scaledPageHeightPx, canvas.height - sourceY)

    const chunkCanvas = document.createElement('canvas')
    chunkCanvas.width = canvas.width
    chunkCanvas.height = sourceHeight

    const context = chunkCanvas.getContext('2d')
    // Chunks are handed to jsPDF as JPEG, which has no alpha: any pixel left transparent here
    // would be written out as black. Paint the page white first so a gap shows up as blank paper.
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, chunkCanvas.width, chunkCanvas.height)
    context.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight)

    chunks.push(chunkCanvas)
  }

  return chunks
}
